import React from 'react';
import PropTypes from 'prop-types';
import Button from '../../../../ui/canon/Button';

// "Check your email" after an identity sign-up or login under
// require_identity_activation (361). Presentational: the countdown and the
// resend request live in ActivationCheckEmailContainer.
//
// `embedded` renders it inside the onboarding wizard's step 1 card, styled like
// the wizard's own header; otherwise it's a standalone page.
const ActivationCheckEmail = ({
  email, secondsLeft, sending, notice, error, onResend, onStartOver, loginUrl, embedded,
}) => {
  const waiting = secondsLeft > 0;
  // The cooldown is 60s, so a longer wait means the hourly limit was hit.
  const overHourlyLimit = secondsLeft > 60;
  let resendLabel = 'Resend email';
  if (sending) resendLabel = 'Sending…';
  else if (waiting && !overHourlyLimit) resendLabel = `Resend in ${secondsLeft}s`;

  const sentTo = email ? (
    <>
      We sent an activation link to <strong className="text-succession-text-primary">{email}</strong>.
    </>
  ) : (
    'We sent you an activation link.'
  );
  const clickToActivate = 'Click it to activate your account and continue.';

  const heading = embedded ? (
    <div className="text-center mb-10 border-b pb-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">Check your email</h1>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">{sentTo}</p>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-2">{clickToActivate}</p>
    </div>
  ) : (
    <>
      <h1 className="text-2xl font-semibold text-succession-text-primary mb-3">Check your email</h1>
      <p className="text-succession-text-secondary mb-2">{sentTo}</p>
      <p className="text-succession-text-secondary mb-6">{clickToActivate}</p>
    </>
  );

  const content = (
    <>
      {heading}
      <p className="text-sm text-succession-text-secondary mb-3">Didn&apos;t receive an email?</p>
      <Button size="lg" onClick={onResend} disabled={sending || waiting} aria-live="polite">
        {resendLabel}
      </Button>

      {overHourlyLimit && (
        <p className="mt-4 text-sm text-succession-text-secondary" role="status">
          You&apos;ve reached the limit for resending emails. Please try again in an hour.
        </p>
      )}
      {notice && (
        <p className="mt-4 text-sm text-succession-success" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="mt-4 text-sm text-succession-error" role="alert">
          {error}
        </p>
      )}

      <p className="mt-6 text-sm text-succession-text-muted">
        The link works once and expires after 24 hours. If you don&apos;t see it, check your spam folder.
      </p>
      <p className="mt-4 text-sm space-x-4">
        {onStartOver && (
          <button type="button" onClick={onStartOver} className="text-succession-text-secondary underline">
            Use a different email
          </button>
        )}
        <a href={loginUrl} className="text-succession-text-secondary underline">
          Back to login
        </a>
      </p>
    </>
  );

  if (embedded) return <div className="text-center">{content}</div>;

  return (
    <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-succession-bg-primary border border-succession-border rounded-lg shadow-succession-md p-8 text-center">
        {content}
      </div>
    </div>
  );
};

ActivationCheckEmail.propTypes = {
  email: PropTypes.string,
  secondsLeft: PropTypes.number.isRequired,
  sending: PropTypes.bool,
  notice: PropTypes.string,
  error: PropTypes.string,
  onResend: PropTypes.func.isRequired,
  onStartOver: PropTypes.func,
  loginUrl: PropTypes.string.isRequired,
  embedded: PropTypes.bool,
};

ActivationCheckEmail.defaultProps = {
  email: null,
  sending: false,
  notice: null,
  error: null,
  onStartOver: null,
  embedded: false,
};

export default ActivationCheckEmail;
